import { MSerializer, MSerializers, ValidationError } from "@legacy-megarax/serializers";

import { Failure, Ok, Result } from "@megaron/result";

export interface ResourceAction<TResponse, TQuery, TReqBody> {
  readonly path: string;
  readonly name: string;
  readonly method: "get" | "post" | "put" | "delete";
  readonly responseSerializer: MSerializer<TResponse>;
  readonly querySerializer?: MSerializer<TQuery>;
  readonly requestBodySerializer?: MSerializer<TReqBody>;
}

export interface ResourceParamNode<TParam, TNestedActions extends ResourceNodes> {
  readonly paramPattern: string;
  readonly paramName: string;
  readonly paramSerializer: MSerializer<TParam>;
  readonly children: TNestedActions;
  readonly path?: string;
}

export interface NestedResourceNode<TNestedActions extends ResourceNodes> {
  path: string;
  children: TNestedActions;
}

export type ResourceNode = ResourceAction<any, any, any> | ResourceParamNode<any, any> | NestedResourceNode<any>;

export const isNodeAction = (node: ResourceNode): node is ResourceAction<any, any, any> => "method" in node;
export const isNodeParam = (node: ResourceNode): node is ResourceParamNode<any, any> => "paramSerializer" in node;
export const isNodeNestedResource = (node: ResourceNode): node is NestedResourceNode<any> =>
  "path" in node && "children" in node;

export interface ResourceNodes {
  [name: string]: ResourceNode;
}

const idParamSerialzier: MSerializer<number> = {
  forceDeserialize: (raw: any) => {
    if (Number.isInteger(parseInt(raw))) return parseInt(raw);
    throw new ValidationError();
  },
  serialize: (id: number) => id,
};

export const StringResourceParam = <TNestedActions extends ResourceNodes>(
  paramName: string,
  path: string,
  nested: TNestedActions,
): ResourceParamNode<string, TNestedActions> => ({
  children: nested,
  paramName,
  paramPattern: "\\b[a-zA-Z0-9]+\\b",
  paramSerializer: MSerializers.string,
  path,
});

export const IntegerStringResourceParam = <TNestedActions extends ResourceNodes>(
  paramName: string,
  nested: TNestedActions,
): ResourceParamNode<string, TNestedActions> => ({
  children: nested,
  paramName,
  paramPattern: "\\d+",
  paramSerializer: MSerializers.string,
});

export const IdResourceParam = <TNestedActions extends ResourceNodes>(
  paramName: string,
  nested: TNestedActions,
): ResourceParamNode<number, TNestedActions> => ({
  children: nested,
  paramName,
  paramPattern: "\\d+",
  paramSerializer: idParamSerialzier,
});

export const UuidResourceParam = <TNestedActions extends ResourceNodes>(
  paramName: string,
  nested: TNestedActions,
): ResourceParamNode<string, TNestedActions> => ({
  paramName,
  children: nested,
  paramPattern: "\\b[0-9a-f]{8}\\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\\b[0-9a-f]{12}\\b",
  paramSerializer: MSerializers.uuid(),
});

export const ResourceAction = <TResponse, TQuery = undefined, TReqBody = undefined>({
  path,
  name,
  method,
  responseSerializer = MSerializers.identity(),
  querySerializer,
  requestBodySerializer,
}: {
  name: string;
  path?: string;
  method?: "get" | "post" | "put" | "delete";
  responseSerializer?: MSerializer<TResponse>;
  querySerializer?: MSerializer<TQuery>;
  requestBodySerializer?: MSerializer<TReqBody>;
}): ResourceAction<TResponse, TQuery, TReqBody> => ({
  method: method ?? "get",
  name: name,
  path: path ?? name,
  responseSerializer,
  querySerializer,
  requestBodySerializer,
});

export const ResourceActionV2 = <TResValue, TResError = never, TQuery = undefined, TReqBody = undefined>({
  path,
  name,
  method,
  responseValueSerializer = MSerializers.identity(),
  responseErrorSerializer = MSerializers.identity(),
  querySerializer,
  requestBodySerializer,
}: {
  name: string;
  path?: string;
  method?: "get" | "post" | "put" | "delete";
  responseValueSerializer?: MSerializer<TResValue>;
  responseErrorSerializer?: MSerializer<TResError>;
  querySerializer?: MSerializer<TQuery>;
  requestBodySerializer?: MSerializer<TReqBody>;
}): ResourceAction<Ok<TResValue> | Failure<TResError>, TQuery, TReqBody> => ({
  method: method ?? "get",
  name: name,
  path: path ?? name,
  responseSerializer: MSerializers.result<TResValue, TResError>(responseValueSerializer, responseErrorSerializer),
  querySerializer,
  requestBodySerializer,
});

// TODO rename to NestedResource
export const NestedResourceNode = <TNested extends ResourceNodes>(
  path: string,
  nested: TNested,
): NestedResourceNode<TNested> => ({
  path,
  children: nested,
});

export interface BaseResource<TResource extends ResourceNodes> {
  nodes: TResource;
  basePath: string[];
}

export const BaseResource = <TResource extends ResourceNodes>(
  basePath: string[],
  actions: TResource,
): BaseResource<TResource> => ({ nodes: actions, basePath });
